/***
 *
 *   SIDEBAR CONTEXT
 *   Collapsed / expanded state for the app sidebar, shared by the sidebar and
 *   by the collapse toggle in the top bar.
 *
 *   The reference keeps this in a Zustand store
 *   (ui-reference/src/stores/sidebar-store.ts). There is no Zustand here, so
 *   it is a context plus localStorage — which also gives the persistence the
 *   reference gets from `persist`.
 *
 *   Cmd/Ctrl+B toggles, ported from `AppLayout` in
 *   ui-reference/src/components/layouts/app-layout.tsx, including its
 *   `isEditableTarget` guard so the shortcut does not fire mid-typing (Ctrl+B
 *   is bold in a rich text field, and the ticket composer has one).
 *
 **********/

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const SIDEBAR_STORAGE_KEY = 'valetify-sidebar-collapsed';

function isEditableTarget(target) {
    if (!(target instanceof HTMLElement)) return false;

    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (target.isContentEditable) return true;

    return !!target.closest('input, textarea, select, [contenteditable="true"]');
}

function getStoredCollapsed() {
    try {
        return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch (err) {
        // private browsing / disabled storage — start expanded
        return false;
    }
}

export const SidebarContext = createContext({
    isCollapsed: false,
    toggle: () => {},
    setCollapsed: () => {},
});

export const SidebarContextProvider = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = useState(getStoredCollapsed);

    const setCollapsed = useCallback((next) => {
        setIsCollapsed(next);

        try {
            localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
        } catch (err) {
            // not persisting is survivable — the state still applies this session
        }
    }, []);

    const toggle = useCallback(() => setCollapsed(!isCollapsed), [isCollapsed, setCollapsed]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key?.toLowerCase() !== 'b') return;
            if (!(e.metaKey || e.ctrlKey)) return;
            if (isEditableTarget(e.target)) return;

            e.preventDefault();
            toggle();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [toggle]);

    return (
        <SidebarContext.Provider value={{ isCollapsed, toggle, setCollapsed }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebarContext = () => {
    return useContext(SidebarContext);
};
